export interface IUserCreationErrorModalContent {
  description: string[];
  heading: string;
}

const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
const TAG_PREFIX_PATTERN = /^\[[^\]]+\]\s*/;

/**
 * Strips internal tags and SMTP continuation codes from a backend error string.
 */
function normalizeBackendErrorMessage(message: string): string {
  return message
    .replace(TAG_PREFIX_PATTERN, '')
    .replace(/\d{3}-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Resolves the recipient email from backend messages or the create target.
 */
function resolveRecipientEmail(messages: string[], userEmail?: string): string | undefined {
  if (userEmail) {
    return userEmail;
  }
  for (const message of messages) {
    const match = message.match(EMAIL_PATTERN);
    if (match) {
      return match[0];
    }
  }
  return undefined;
}

/**
 * Returns user-friendly modal copy for user-creation failures.
 */
export function formatUserCreationErrorModalContent(
  messages: string[] | undefined,
  userEmail?: string,
): IUserCreationErrorModalContent {
  const rawMessages = messages ?? [];
  const raw = rawMessages.join(' ').trim();
  const normalized = normalizeBackendErrorMessage(raw);
  const lower = normalized.toLowerCase();
  const email = resolveRecipientEmail(rawMessages, userEmail);

  if (!raw) {
    return {
      heading: 'Something went wrong while creating the user.',
      description: ['Please try again. If the problem continues, contact Gallix support.'],
    };
  }

  if (
    lower.includes('send welcome email') ||
    lower.includes('welcome email') ||
    lower.includes('recipients were rejected') ||
    lower.includes('could not deliver mail')
  ) {
    const emailPhrase = email ? ` to ${email}` : '';
    return {
      heading: 'Welcome email could not be sent',
      description: [
        `We could not deliver the welcome email${emailPhrase}.`,
        'Please check the email address is correct and try again.',
        'If the problem continues, contact Gallix support.',
      ],
    };
  }

  if (lower.includes('failed to create user')) {
    const detail = normalized.replace(/^failed to create user:\s*/i, '').trim();
    if (detail.length > 0 && detail.length <= 120 && !detail.includes('mail server') && !/\d{3}/.test(detail)) {
      return {
        heading: 'Could not create user',
        description: [detail, 'Please try again. If the problem continues, contact Gallix support.'],
      };
    }
    return {
      heading: 'Could not create user',
      description: [
        'The user account could not be created.',
        'Please try again. If the problem continues, contact Gallix support.',
      ],
    };
  }

  if (normalized.length <= 100 && !/\d{3}/.test(normalized) && !lower.includes('mail server')) {
    return {
      heading: 'Could not create user',
      description: [normalized, 'If the problem continues, contact Gallix support.'],
    };
  }

  return {
    heading: 'Could not create user',
    description: [
      'Something went wrong while creating the user.',
      'Please try again. If the problem continues, contact Gallix support.',
    ],
  };
}
